import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { SeatMapView, useSeatSelection } from '@bre-ticketing/seatmap';
import type { ManifestGeometry, SeatGeometry } from '@bre-ticketing/seatmap';
import type { ApiError, InventoryUnitRow, ManifestLayout } from '../api';
import { endpoints, inventoryEndpoints, organizationEndpoints } from '../api';
import { paymentEndpoints } from '../api-payments';
import type { WindowCartView } from '../api-payments';
import { groupSalesEndpoints } from '../api-group-sales';
import type { GroupHold } from '../api-group-sales';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';
import { INPUT, dollars } from './event/shared';
import { dateWords } from '../terms';

/**
 * Admin → Group holds (D-99, D-336).
 *
 * A group hold is an unpaid order with a deadline. This page lists them,
 * nearest deadline first, and builds a new one in three steps: who the group
 * is, which seats, and by when they pay.
 *
 * The seats go into the ordinary staff cart, the same one New Order builds
 * from, so seats already held there carry into the builder and "Hold for a
 * group instead" on New Order lands here with them.
 */

const TONE: Record<string, Tone> = { HELD: 'warn', PAID: 'ok', RELEASED: 'neutral', CANCELLED: 'neutral' };

/** "3 days left", "Due today", "2 days late": what a list of deadlines is for. */
export function urgency(dueAt: string | null, status: string): { text: string; tone: Tone } | null {
  if (!dueAt || status !== 'HELD') return null;
  const days = Math.ceil((new Date(dueAt).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return { text: `${-days} day${days === -1 ? '' : 's'} late`, tone: 'bad' };
  if (days === 0) return { text: 'Due today', tone: 'bad' };
  if (days <= 3) return { text: `${days} day${days === 1 ? '' : 's'} left`, tone: 'warn' };
  return { text: `${days} days left`, tone: 'neutral' };
}

export default function GroupHoldsPage() {
  const can = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState('HELD');
  const [search, setSearch] = useState('');
  const building = params.get('new') === '1';
  const from = params.get('from');

  const holds = useQuery<{ group_holds: GroupHold[] }, ApiError>({
    queryKey: ['group-holds', status],
    queryFn: () => groupSalesEndpoints.list(status === 'ALL' ? {} : { status }),
  });

  if (!can('group_sales.view')) return <NotPermitted permission="group_sales.view" />;
  const all = holds.data?.group_holds ?? [];
  const needle = search.trim().toLowerCase();
  const rows = (needle ? all.filter((row) => `${row.organization_name} ${row.contact_name} ${row.event_title} ${row.order_number}`.toLowerCase().includes(needle)) : all)
    .slice()
    // What needs chasing first: the nearest deadline at the top.
    .sort((a, b) => (a.payment_due_at ?? '9').localeCompare(b.payment_due_at ?? '9'));
  const held = all.filter((row) => row.status === 'HELD');
  const owed = held.reduce((sum, row) => sum + row.balance_due_cents, 0);
  const seats = held.reduce((sum, row) => sum + row.seat_count, 0);
  const late = held.filter((row) => row.payment_due_at && row.payment_due_at < new Date().toISOString()).length;
  const canBuild = can('group_sales.manage') && can('box_office.sell');

  const closeBuilder = () => setParams({});

  return (
    <div className="space-y-4">
      {holds.error ? <ErrorNote>{holds.error.message}</ErrorNote> : null}

      <Panel
        title="Group holds"
        description="Seats held for a group with a date to pay by. The seats are off sale; nothing becomes a ticket until the money arrives, and the seats go back on their own if it never does."
        actions={
          canBuild ? (
            <Button variant="primary" onClick={() => (building ? closeBuilder() : setParams({ new: '1' }))}>
              {building ? 'Close' : 'Hold seats for a group'}
            </Button>
          ) : null
        }
      >
        <div className="flex flex-wrap gap-8 px-5 py-4">
          <Stat label="Currently held" value={String(seats)} detail={`${held.length} group${held.length === 1 ? '' : 's'}`} />
          <Stat label="Still owed" value={dollars(owed)} />
          <Stat label="Past their date" value={String(late)} detail={late ? 'released after their grace period' : 'none'} />
        </div>
      </Panel>

      {building && canBuild ? (
        <GroupHoldBuilder
          fromHoldId={from}
          onCancel={closeBuilder}
          onCreated={(id, link) => {
            void queryClient.invalidateQueries({ queryKey: ['group-holds'] });
            navigate(`/group-holds/${id}`, { state: { link } });
          }}
        />
      ) : null}

      <Panel
        title="Holds"
        actions={
          <div className="flex flex-wrap gap-2">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search group, event or order" aria-label="Search holds" className={`w-56 ${INPUT}`} />
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={INPUT} aria-label="Status">
              <option value="HELD">Held</option>
              <option value="PAID">Paid</option>
              <option value="RELEASED">Released</option>
              <option value="ALL">All</option>
            </select>
          </div>
        }
      >
        <Table headers={['Group', 'Event', 'Seats', 'Owed', 'Pay by', 'Status', 'Order']}>
          {rows.length === 0 ? (
            <EmptyRow columns={7}>{holds.isLoading ? 'Loading…' : needle ? 'No hold matches that.' : 'No group holds.'}</EmptyRow>
          ) : (
            rows.map((row) => {
              const due = urgency(row.payment_due_at, row.status);
              return (
                <tr key={row.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-page" onClick={() => navigate(`/group-holds/${row.id}`)}>
                  <td className="px-5 py-3 text-sm">
                    <a href={`/group-holds/${row.id}`} onClick={(e) => { e.preventDefault(); navigate(`/group-holds/${row.id}`); }} className="font-medium text-live hover:underline">
                      {row.organization_name}
                    </a>
                    <div className="text-xs text-ink-500">
                      {row.contact_name} · {row.contact_email}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <div>{row.event_title}</div>
                    <div className="text-xs text-ink-500">{formatWhen(row.performance_starts_at)}</div>
                  </td>
                  <td className="tabular px-5 py-3 text-sm">{row.seat_count}</td>
                  <td className={`tabular px-5 py-3 text-sm ${row.balance_due_cents > 0 ? 'font-medium' : 'text-ink-400'}`}>
                    {dollars(row.balance_due_cents)}
                    <div className="text-xs font-normal text-ink-500">of {dollars(row.total_cents)}</div>
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <div>{row.payment_due_at ? formatWhen(row.payment_due_at) : '—'}</div>
                    {due ? <Badge tone={due.tone}>{due.text}</Badge> : null}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <Badge tone={TONE[row.status] ?? 'neutral'}>{row.status.toLowerCase()}</Badge>
                    {row.release_reason ? <div className="mt-1 text-xs text-ink-500">{row.release_reason}</div> : null}
                  </td>
                  <td className="px-5 py-3 font-mono text-sm">{row.order_number}</td>
                </tr>
              );
            })
          )}
        </Table>
      </Panel>
    </div>
  );
}

function Stat({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div>
      <div className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</div>
      <div className="tabular mt-1 text-2xl font-semibold">{value}</div>
      {detail ? <div className="text-xs text-ink-500">{detail}</div> : null}
    </div>
  );
}

// --- The builder ---------------------------------------------------------------------------------

interface Who {
  organization_id: string | null;
  organization_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  save_organization: boolean;
}

const EMPTY_WHO: Who = { organization_id: null, organization_name: '', contact_name: '', contact_email: '', contact_phone: '', save_organization: true };

/** Today where the browser is, as the date input wants it. */
const todayYmd = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

function GroupHoldBuilder({ fromHoldId, onCancel, onCreated }: { fromHoldId: string | null; onCancel: () => void; onCreated: (id: string, link: string | null) => void }) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [who, setWho] = useState<Who>(EMPTY_WHO);
  const [error, setError] = useState<string | null>(null);

  // "Hold more seats for this group" starts from the group it came from.
  const source = useQuery({ queryKey: ['group-hold', fromHoldId], queryFn: () => groupSalesEndpoints.detail(fromHoldId!), enabled: Boolean(fromHoldId) });
  useEffect(() => {
    const hold = source.data?.group_hold;
    if (!hold) return;
    setWho({ organization_id: hold.organization_id, organization_name: hold.organization_name, contact_name: hold.contact_name, contact_email: hold.contact_email, contact_phone: hold.contact_phone ?? '', save_organization: false });
    setStep(2);
  }, [source.data]);

  const whoReady = who.organization_name.trim() && who.contact_name.trim() && /\S+@\S+\.\S+/.test(who.contact_email.trim());

  return (
    <Panel title="Hold seats for a group" description="Who the group is, which seats, and by when they pay. Nothing is charged; the seats come off sale when you hold them.">
      <ol className="flex flex-wrap gap-2 border-b border-line px-5 py-3 text-sm">
        {(['Group', 'Seats', 'Terms'] as const).map((label, index) => {
          const n = (index + 1) as 1 | 2 | 3;
          const reachable = n === 1 || (n === 2 && whoReady) || n === step;
          return (
            <li key={label}>
              <button
                type="button"
                disabled={!reachable}
                onClick={() => setStep(n)}
                className={`rounded-full px-3 py-1 ${step === n ? 'bg-accent text-white' : 'border border-line text-ink-600 disabled:opacity-50'}`}
                aria-current={step === n ? 'step' : undefined}
              >
                {n}. {label}
              </button>
            </li>
          );
        })}
      </ol>
      {error ? (
        <div className="px-5 pt-4">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      {step === 1 ? <WhoStep who={who} onChange={setWho} ready={Boolean(whoReady)} onCancel={onCancel} onNext={() => { setError(null); setStep(2); }} /> : null}
      {step === 2 ? <SeatsStep onError={setError} onBack={() => setStep(1)} onNext={() => { setError(null); setStep(3); }} /> : null}
      {step === 3 ? <TermsStep who={who} onError={setError} onBack={() => setStep(2)} onCreated={onCreated} /> : null}
    </Panel>
  );
}

function WhoStep({ who, onChange, ready, onCancel, onNext }: { who: Who; onChange: (who: Who) => void; ready: boolean; onCancel: () => void; onNext: () => void }) {
  const can = usePermissions();
  const [query, setQuery] = useState('');
  const organizations = useQuery({ queryKey: ['organizations'], queryFn: organizationEndpoints.list, enabled: can('events.view') });
  const chosen = useQuery({ queryKey: ['organization', who.organization_id], queryFn: () => organizationEndpoints.get(who.organization_id!), enabled: Boolean(who.organization_id) });
  const needle = query.trim().toLowerCase();
  const matches = needle ? (organizations.data?.organizations ?? []).filter((o) => o.active && o.name.toLowerCase().includes(needle)).slice(0, 8) : [];
  const set = (patch: Partial<Who>) => onChange({ ...who, ...patch });

  return (
    <div className="space-y-4 px-5 py-4">
      {who.organization_id ? (
        <div className="flex flex-wrap items-center gap-3 rounded border border-line bg-page px-3 py-2 text-sm">
          <span>
            Holding for <strong>{who.organization_name}</strong>
          </span>
          <Button onClick={() => onChange(EMPTY_WHO)}>Choose another</Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Field label="Find a saved organization" htmlFor="gh-find">
            <input id="gh-find" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Burlington High School" className={`w-80 ${INPUT}`} autoComplete="off" />
          </Field>
          {matches.length > 0 ? (
            <ul className="w-80 divide-y divide-line rounded border border-line">
              {matches.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-page"
                    onClick={() => {
                      setQuery('');
                      onChange({ organization_id: o.id, organization_name: o.name, contact_name: '', contact_email: o.primary_email ?? '', contact_phone: o.phone ?? '', save_organization: false });
                    }}
                  >
                    <span className="font-medium">{o.name}</span>
                    {o.primary_email ? <span className="ml-2 text-xs text-ink-500">{o.primary_email}</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : needle ? (
            <p className="text-xs text-ink-500">No saved organization by that name. Fill in the group below.</p>
          ) : null}
        </div>
      )}

      {who.organization_id && (chosen.data?.contacts.length ?? 0) > 0 ? (
        <fieldset className="space-y-1">
          <legend className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Contact</legend>
          {chosen.data!.contacts.map((contact) => (
            <label key={contact.id} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="gh-contact"
                checked={who.contact_email.toLowerCase() === contact.email.toLowerCase() && who.contact_name === contact.name}
                onChange={() => set({ contact_name: contact.name, contact_email: contact.email, contact_phone: contact.phone ?? '' })}
              />
              {contact.name}
              <span className="text-ink-500">
                {contact.title ? `${contact.title} · ` : ''}
                {contact.email}
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}

      <div className="flex flex-wrap gap-4">
        {!who.organization_id ? (
          <Field label="Group" htmlFor="gh-org">
            <input id="gh-org" value={who.organization_name} onChange={(e) => set({ organization_name: e.target.value })} placeholder="Burlington High School" className={`w-64 ${INPUT}`} />
          </Field>
        ) : null}
        <Field label="Contact" htmlFor="gh-name">
          <input id="gh-name" value={who.contact_name} onChange={(e) => set({ contact_name: e.target.value })} className={`w-48 ${INPUT}`} />
        </Field>
        <Field label="Email" htmlFor="gh-email">
          <input id="gh-email" type="email" value={who.contact_email} onChange={(e) => set({ contact_email: e.target.value })} className={`w-64 ${INPUT}`} />
        </Field>
        <Field label="Phone" htmlFor="gh-phone">
          <input id="gh-phone" value={who.contact_phone} onChange={(e) => set({ contact_phone: e.target.value })} className={`w-40 ${INPUT}`} />
        </Field>
      </div>
      {!who.organization_id ? (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={who.save_organization} onChange={(e) => set({ save_organization: e.target.checked })} /> Save this group as an organization, so it is found next time
        </label>
      ) : null}

      <div className="flex gap-2">
        <Button variant="primary" disabled={!ready} onClick={onNext}>
          Next: seats
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

const FILL = { available: '#7ac47f', mine: '#3b6fd8', held: '#d99a2b', gone: '#d9d9d9' };

function SeatsStep({ onError, onBack, onNext }: { onError: (message: string | null) => void; onBack: () => void; onNext: () => void }) {
  const queryClient = useQueryClient();
  const [performanceId, setPerformanceId] = useState('');
  const [ticketTypeId, setTicketTypeId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [mode, setMode] = useState<'best' | 'map'>('best');
  const [quantity, setQuantity] = useState('10');
  const selection = useSeatSelection();

  const performances = useQuery({ queryKey: ['new-order', 'performances'], queryFn: paymentEndpoints.newPerformances });
  const cart = useQuery<WindowCartView, ApiError>({ queryKey: ['new-order', 'cart'], queryFn: paymentEndpoints.cart });
  const setCart = (view: WindowCartView) => queryClient.setQueryData(['new-order', 'cart'], view);
  const performance = (performances.data?.performances ?? []).find((p) => p.id === performanceId) ?? null;
  const detail = useQuery({ queryKey: ['new-order', 'performance', performanceId], queryFn: () => paymentEndpoints.performance(performanceId), enabled: Boolean(performanceId) });
  const layout = useQuery<ManifestLayout>({ queryKey: ['manifest-layout', performance?.manifest_id], queryFn: () => endpoints.manifestLayout(performance!.manifest_id!), enabled: mode === 'map' && Boolean(performance?.manifest_id) });
  const units = useQuery({
    queryKey: ['inventory-units', performance?.event_id, performanceId],
    queryFn: () => inventoryEndpoints.units(performance!.event_id, performanceId),
    enabled: mode === 'map' && Boolean(performance),
    refetchInterval: 5000,
  });

  // Seats already in the cart pick the performance, so a cart started on New Order carries straight in.
  useEffect(() => {
    const first = cart.data?.performances[0]?.performance_id;
    if (first && !performanceId) setPerformanceId(first);
  }, [cart.data, performanceId]);
  useEffect(() => {
    const types = detail.data?.ticket_types ?? [];
    if (types.length > 0 && !types.some((t) => t.id === ticketTypeId)) setTicketTypeId(types.find((t) => !t.is_comp_type)?.id ?? types[0]!.id);
  }, [detail.data, ticketTypeId]);

  const mine = useMemo(() => new Set((cart.data?.performances ?? []).flatMap((p) => p.seats.map((s) => s.unit_id))), [cart.data]);
  const unitBySeat = useMemo(() => new Map((units.data?.units ?? []).filter((u: InventoryUnitRow) => u.seat_id).map((u: InventoryUnitRow) => [u.seat_id!, u])), [units.data]);
  const seatFills = useMemo(() => {
    const fills: Record<string, string> = {};
    for (const [seatId, unit] of unitBySeat) {
      fills[seatId] = mine.has(unit.unit_id) ? FILL.mine : unit.state === 'AVAILABLE' && !unit.cart_held ? FILL.available : unit.state === 'HELD' ? FILL.held : FILL.gone;
    }
    return fills;
  }, [unitBySeat, mine]);
  const pickable = useMemo(
    () => Array.from(selection.selected, (seatId) => unitBySeat.get(seatId)).filter((u): u is InventoryUnitRow => Boolean(u) && u!.state === 'AVAILABLE' && !u!.cart_held && !mine.has(u!.unit_id)),
    [selection.selected, unitBySeat, mine],
  );

  const holdBest = useMutation({
    mutationFn: () => {
      const n = Number(quantity);
      if (!Number.isInteger(n) || n < 1) throw new Error('Say how many seats');
      return paymentEndpoints.hold({ performance_id: performanceId, ticket_type_id: ticketTypeId, mode: 'best', quantity: Math.min(n, 50), allow_split: true, ...(zoneId ? { price_zone_ids: [zoneId] } : {}) });
    },
    onSuccess: (r) => {
      onError(null);
      setCart(r.cart);
    },
    onError: (cause: Error) => onError(cause.message),
  });
  const holdPicked = useMutation({
    mutationFn: async () => {
      // The cart takes at most 50 seats a call; a bus-load is held in pieces.
      let view: WindowCartView | null = null;
      const ids = pickable.map((u) => u.unit_id);
      for (let i = 0; i < ids.length; i += 50) view = (await paymentEndpoints.holdSeats({ performance_id: performanceId, ticket_type_id: ticketTypeId, unit_ids: ids.slice(i, i + 50) })).cart;
      return view;
    },
    onSuccess: (view) => {
      onError(null);
      if (view) setCart(view);
      selection.clear();
      void units.refetch();
    },
    onError: (cause: Error) => onError(cause.message),
  });
  const release = useMutation({
    mutationFn: (input: { performance_id: string; unit_ids?: string[] }) => paymentEndpoints.releaseSeats(input),
    onSuccess: (r) => {
      setCart(r.cart);
      void units.refetch();
    },
    onError: (cause: Error) => onError(cause.message),
  });

  // Picking forty seats takes longer than a cart hold lasts; keep it alive while the builder is open.
  useEffect(() => {
    const expires = cart.data?.expires_at;
    if (!expires) return;
    const wait = Math.max(5_000, new Date(expires).getTime() - Date.now() - 90_000);
    const timer = window.setTimeout(() => void paymentEndpoints.extendCart().then(() => cart.refetch()).catch(() => undefined), wait);
    return () => window.clearTimeout(timer);
  }, [cart.data?.expires_at, cart]);

  const cartPerformances = cart.data?.performances ?? [];
  const seatCount = cartPerformances.reduce((n, p) => n + p.seats.length, 0);
  const geometry = layout.data as unknown as ManifestGeometry | undefined;
  const prices = detail.data?.prices[ticketTypeId] ?? {};

  return (
    <div className="grid gap-4 px-5 py-4 lg:grid-cols-[1fr_20rem]">
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={`${dateWords().One}`} htmlFor="gh-perf">
            <select id="gh-perf" className={INPUT} value={performanceId} onChange={(e) => { setPerformanceId(e.target.value); setZoneId(''); selection.clear(); }}>
              <option value="">Choose…</option>
              {(performances.data?.performances ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.event_title} · {formatWhen(p.starts_at)}
                </option>
              ))}
            </select>
          </Field>
          {detail.data ? (
            <Field label="Ticket type" htmlFor="gh-type">
              <select id="gh-type" className={INPUT} value={ticketTypeId} onChange={(e) => setTicketTypeId(e.target.value)}>
                {detail.data.ticket_types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          {performanceId ? (
            <div className="flex rounded border border-line text-sm" role="group" aria-label="How to choose seats">
              <button type="button" className={`px-3 py-1.5 ${mode === 'best' ? 'bg-accent text-white' : ''}`} onClick={() => setMode('best')}>
                Best available
              </button>
              <button type="button" disabled={!performance?.manifest_id} className={`px-3 py-1.5 disabled:opacity-50 ${mode === 'map' ? 'bg-accent text-white' : ''}`} onClick={() => setMode('map')}>
                Pick on the map
              </button>
            </div>
          ) : null}
        </div>

        {performanceId && mode === 'best' && detail.data ? (
          <div className="flex flex-wrap items-end gap-3">
            <Field label="How many" htmlFor="gh-qty">
              <input id="gh-qty" className={`tabular w-20 ${INPUT}`} value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ''))} inputMode="numeric" />
            </Field>
            <Field label="Price level" htmlFor="gh-zone">
              <select id="gh-zone" className={INPUT} value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
                <option value="">Any</option>
                {detail.data.zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                    {prices[z.id] ? ` · ${dollars(prices[z.id]!.total_cents)}` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Button variant="primary" disabled={holdBest.isPending || !ticketTypeId} onClick={() => holdBest.mutate()}>
              {holdBest.isPending ? 'Holding…' : `Hold ${Math.min(Number(quantity) || 0, 50)} best available`}
            </Button>
            {Number(quantity) > 50 ? <p className="text-xs text-ink-500">50 at a time; hold again for the rest.</p> : null}
          </div>
        ) : null}

        {performanceId && mode === 'map' ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3 text-xs text-ink-600">
              <Legend color={FILL.available} label="Available" />
              <Legend color={FILL.mine} label="In this hold" />
              <Legend color={FILL.held} label="House hold" />
              <Legend color={FILL.gone} label="Sold or unavailable" />
              <span>Click seats, or drag a box around a block. Shift-click adds to what is selected.</span>
            </div>
            {geometry ? (
              <SeatMapView
                layout={geometry}
                mode="edit"
                backgroundUrl={layout.data?.manifest.background_url ?? null}
                seatFills={seatFills}
                selectedSeatIds={selection.selected}
                onSeatClick={(seat: SeatGeometry, modifiers: { shiftKey: boolean; metaKey: boolean }) => selection.clickSeat(seat, modifiers)}
                onMarqueeSelect={selection.marquee}
                className="max-h-[32rem] overflow-hidden rounded border border-line"
              />
            ) : (
              <p className="text-sm text-ink-500">{layout.isLoading ? 'Loading the map…' : `This ${dateWords().one} has no seat map; use best available.`}</p>
            )}
            <div className="flex items-center gap-2">
              <Button variant="primary" disabled={pickable.length === 0 || holdPicked.isPending} onClick={() => holdPicked.mutate()}>
                {holdPicked.isPending ? 'Holding…' : `Hold ${pickable.length} selected seat${pickable.length === 1 ? '' : 's'}`}
              </Button>
              {selection.selected.size > pickable.length ? <span className="text-xs text-ink-500">{selection.selected.size - pickable.length} selected seat(s) are not available and will be skipped.</span> : null}
            </div>
          </div>
        ) : null}
      </div>

      <aside className="space-y-3 rounded border border-line bg-page p-3">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold">In this hold</h3>
          <span className="tabular text-sm">{seatCount} seat{seatCount === 1 ? '' : 's'}</span>
        </div>
        {cartPerformances.length === 0 ? <p className="text-sm text-ink-500">Nothing yet. Hold seats on the left.</p> : null}
        {cartPerformances.length > 1 ? <p className="text-xs text-bad">A group hold covers one {dateWords().one}. Give back the seats for the other one, or make a second hold.</p> : null}
        {cartPerformances.map((p) => (
          <div key={p.performance_id} className="space-y-1">
            <p className="text-xs font-medium">
              {p.title} · {formatWhen(p.starts_at)}
            </p>
            <ul className="max-h-64 space-y-0.5 overflow-auto text-xs">
              {p.seats.map((s) => (
                <li key={s.unit_id} className="flex items-center justify-between gap-2">
                  <span>
                    {s.label}
                    {s.ticket_type_name ? <span className="text-ink-500"> · {s.ticket_type_name}</span> : null}
                  </span>
                  <button type="button" className="text-ink-500 hover:text-bad" aria-label={`Give back ${s.label}`} onClick={() => release.mutate({ performance_id: p.performance_id, unit_ids: [s.unit_id] })}>
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className="text-xs text-bad hover:underline" onClick={() => release.mutate({ performance_id: p.performance_id })}>
              Give back all {p.seats.length}
            </button>
          </div>
        ))}
        {cart.data && seatCount > 0 ? <p className="border-t border-line pt-2 text-sm">Total {dollars(cart.data.totals.customer_total_cents)}</p> : null}
        <div className="flex gap-2">
          <Button onClick={onBack}>Back</Button>
          <Button variant="primary" disabled={seatCount === 0 || cartPerformances.length > 1} onClick={onNext}>
            Next: terms
          </Button>
        </div>
      </aside>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-block h-3 w-3 rounded-full" style={{ background: color }} aria-hidden="true" />
      {label}
    </span>
  );
}

function TermsStep({ who, onError, onBack, onCreated }: { who: Who; onError: (message: string | null) => void; onBack: () => void; onCreated: (id: string, link: string | null) => void }) {
  const [dueOn, setDueOn] = useState('');
  const [graceHours, setGraceHours] = useState('24');
  const [note, setNote] = useState('');
  const [sendLink, setSendLink] = useState(true);
  const cart = useQuery<WindowCartView, ApiError>({ queryKey: ['new-order', 'cart'], queryFn: paymentEndpoints.cart });
  const performance = cart.data?.performances[0] ?? null;
  const seats = performance?.seats.length ?? 0;

  const create = useMutation({
    mutationFn: () =>
      groupSalesEndpoints.create({
        organization_name: who.organization_name.trim(),
        organization_id: who.organization_id,
        contact_name: who.contact_name.trim(),
        contact_email: who.contact_email.trim(),
        contact_phone: who.contact_phone.trim() || null,
        due_on: dueOn,
        grace_hours: Number(graceHours) || 0,
        note: note.trim() || null,
        send_link: sendLink,
        save_organization: !who.organization_id && who.save_organization,
      }),
    onSuccess: (result) => onCreated(result.group_hold.id, result.payment_request?.url ?? null),
    onError: (cause: ApiError) => onError(cause.message),
  });

  return (
    <form
      className="space-y-4 px-5 py-4"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <div className="rounded border border-line bg-page px-3 py-2 text-sm">
        <strong>{who.organization_name}</strong> · {who.contact_name} ({who.contact_email})
        <br />
        {performance ? (
          <>
            {seats} seat{seats === 1 ? '' : 's'} for {performance.title}, {formatWhen(performance.starts_at)} · {dollars(cart.data?.totals.customer_total_cents ?? 0)}
          </>
        ) : (
          'No seats are held. Go back a step.'
        )}
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Pay by" htmlFor="gh-due">
          <input id="gh-due" type="date" min={todayYmd()} value={dueOn} onChange={(e) => setDueOn(e.target.value)} required className={INPUT} />
        </Field>
        <Field label="Grace (hours)" htmlFor="gh-grace">
          <input id="gh-grace" value={graceHours} onChange={(e) => setGraceHours(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-24 ${INPUT}`} />
        </Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input type="checkbox" checked={sendLink} onChange={(e) => setSendLink(e.target.checked)} /> Email {who.contact_name.split(' ')[0] || 'the contact'} the payment link now
        </label>
      </div>
      <Field label="Note" htmlFor="gh-note">
        <input id="gh-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Two buses, arriving 6:15" className={`w-full ${INPUT}`} />
      </Field>
      <p className="text-xs text-ink-500">
        {dueOn ? `They have until 11:59 p.m. on ${new Date(`${dueOn}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}. ` : ''}
        Reminders go out a week before, the day before, and the day after. The seats go back on sale {graceHours || '0'} hour{graceHours === '1' ? '' : 's'} after the deadline if nothing has been paid.
      </p>
      <div className="flex gap-2">
        <Button type="button" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" variant="primary" disabled={create.isPending || !dueOn || seats === 0}>
          {create.isPending ? 'Holding…' : `Hold ${seats} seat${seats === 1 ? '' : 's'} for ${who.organization_name || 'the group'}`}
        </Button>
      </div>
    </form>
  );
}
